import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { Spinner } from "@shopify/polaris";
import { AppDispatch } from "configureStore";
import {
  createMedicalCheck,
  fetchMedicalChecksByUser,
  MedicalCheck,
  MedicalCheckEligibility,
} from "services/medical-check.service";
import MedicalCheckBlock, {
  viewMedicalFile,
} from "components/medical-check-block/medical-check-block";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import { API_URL } from "../../constants";
import useIsPhone from "features/files/use-is-phone";

function formatDate(ts?: number | null) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("lt-LT");
}

interface UserProfileMedicalProps {
  companyId: string;
  userId: string;
}

export default function UserProfileMedical({
  companyId,
  userId,
}: UserProfileMedicalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isPhone = useIsPhone();
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState<MedicalCheck[]>([]);
  const [eligibility, setEligibility] = useState<MedicalCheckEligibility | null>(
    null
  );
  const [canApproveMedical, setCanApproveMedical] = useState(false);
  const [canUploadMedical, setCanUploadMedical] = useState(false);
  const [medicalApprovers, setMedicalApprovers] = useState<string[]>([]);
  const [initiating, setInitiating] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchMedicalChecksByUser(companyId, userId);
      setChecks(data.checks || []);
      setEligibility(data.eligibility);
      setCanApproveMedical(data.canApproveMedical);
      setCanUploadMedical(data.canUploadMedical);
      setMedicalApprovers(data.medicalApprovers || []);
    } catch {
      toast.error(t("error"));
      setChecks([]);
      setEligibility(null);
      setCanApproveMedical(false);
      setCanUploadMedical(false);
      setMedicalApprovers([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, userId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const pendingCheck = checks.find((c) => c.status === "pending");
  const currentApproved = checks.find((c) => c.status === "approved");
  const renewalDraft =
    pendingCheck && !pendingCheck.validFrom && !pendingCheck.validUntil
      ? pendingCheck
      : null;
  const activeCheck = renewalDraft || pendingCheck || currentApproved;
  const historyChecks = checks.filter((c) => c.id !== activeCheck?.id);

  const handleViewFile = (fileId: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxComponentProps({ id: fileId, companyId }));
    dispatch(setIsModalxOpen(true));
  };

  const handleDownloadFile = (fileId: string) => {
    window.open(`${API_URL}/${companyId}/files/download/${fileId}`, "_blank");
  };

  const renderFiles = (row: MedicalCheck) => (
    <div className="flex flex-col gap-2">
      {row.files?.map((f) => {
        const fileObj = typeof f.file === "object" ? f.file : null;
        if (!fileObj) return null;
        return (
          <div key={f.id} className="flex items-center gap-2 flex-wrap max-md:gap-3">
            <span className="text-sm text-gray-700">
              {fileObj.name || fileObj.title || t("medical_certificate")}
            </span>
            <button
              className="px-2 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:px-3 max-md:text-sm"
              onClick={() =>
                viewMedicalFile(companyId, fileObj.id, fileObj.title || fileObj.name || "", handleViewFile)
              }
            >
              {t("view")}
            </button>
            <button
              className="px-2 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:px-3 max-md:text-sm"
              onClick={() => handleDownloadFile(fileObj.id)}
            >
              {t("download")}
            </button>
          </div>
        );
      })}
    </div>
  );

  const handleInitiate = async () => {
    setInitiating(true);
    try {
      await createMedicalCheck(companyId, userId);
      toast.success(t("medical_check_renew_started"));
      await loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t("error"));
    } finally {
      setInitiating(false);
    }
  };

  const eligibilityMessage = () => {
    if (!eligibility || eligibility.canInitiate) return null;
    if (eligibility.reason === "pending_exists") {
      return t("medical_renew_pending_exists");
    }
    if (eligibility.reason === "too_early" && eligibility.renewAvailableFrom) {
      return t("medical_renew_too_early", {
        date: formatDate(eligibility.renewAvailableFrom),
      });
    }
    return t("medical_renew_not_available");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!activeCheck && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
          <p className="text-sm text-gray-600">{t("no_medical_check")}</p>
        </div>
      )}

      {activeCheck && (
        <MedicalCheckBlock
          companyId={companyId}
          check={activeCheck}
          canApproveMedical={canApproveMedical}
          canUpload={canUploadMedical && activeCheck.status === "pending"}
          medicalApprovers={medicalApprovers}
          onReload={loadData}
          onViewFile={handleViewFile}
        />
      )}

      {eligibility?.canInitiate && !pendingCheck && (
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          onClick={handleInitiate}
          disabled={initiating}
        >
          {t("start_new_medical_check")}
        </button>
      )}

      {!eligibility?.canInitiate && eligibilityMessage() && (
        <p className="text-sm text-gray-500">{eligibilityMessage()}</p>
      )}

      {historyChecks.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
          <h3 className="text-base font-semibold mb-4">
            {t("medical_check_history")}
          </h3>
          {isPhone ? (
            <ul className="divide-y divide-gray-100">
              {historyChecks.map((row) => (
                <li key={row.id} className="space-y-1 py-3 text-sm">
                  <p className="font-medium capitalize">{row.status}</p>
                  <p className="text-gray-600">
                    {formatDate(row.validFrom)} – {formatDate(row.validUntil)}
                  </p>
                  <p className="text-gray-600">{row.approvedBy?.name || "—"}</p>
                  {renderFiles(row)}
                </li>
              ))}
            </ul>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="py-2 pr-4">{t("status")}</th>
                    <th className="py-2 pr-4">{t("medical_validity")}</th>
                    <th className="py-2 pr-4">{t("approved_by")}</th>
                    <th className="py-2">{t("files")}</th>
                  </tr>
                </thead>
                <tbody>
                  {historyChecks.map((row) => (
                    <tr key={row.id} className="border-b border-gray-100">
                      <td className="py-2 pr-4 capitalize">{row.status}</td>
                      <td className="py-2 pr-4">
                        {formatDate(row.validFrom)} – {formatDate(row.validUntil)}
                      </td>
                      <td className="py-2 pr-4">
                        {row.approvedBy?.name || "—"}
                      </td>
                      <td className="py-2">
                        {renderFiles(row)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
